import React, { useState, useEffect } from "react";
import {
  Settings,
  Bell,
  Moon,
  Sun,
  Globe,
  Shield,
  Palette,
  Database,
  Mail,
  Smartphone,
  Lock,
  Eye,
  EyeOff,
  RefreshCw,
  Download,
  Upload,
  Trash2,
  HelpCircle,
  ChevronDown,
  LogOut,
  MessageCircle,
  AlertCircle,
  CheckCircle2,
  X,
} from "lucide-react";
import PasswordRequirements from "../../../components/PasswordRequirements";
import { isPasswordValid } from "../../../utils/passwordPolicy";
import { useSettingsState } from "../hooks/useSettingsState";
import { getSettingsStyles } from "../styles/settingsStyles";
import { exportSettings } from "../utils/settingsExporter";
import { useLogout } from "../../../hooks/useLogout";
import SaveSuccessModal from "../../../components/SaveSuccessModal";

import { useNotifications } from "../../../context/NotificationContext";

const EnhancedSettingsPage = () => {
  const { logout } = useLogout();           // ← get logout from hook
  const {
    notificationsDisabled, setNotificationsDisabled,
    miniChatDisabled, setMiniChatDisabled,
    clientBadgeDisabled, setClientBadgeDisabled,
  } = useNotifications();
  const {
    theme,
    setTheme,
    language,
    toggleLanguage,
    t,
    isRTL,
    isMobile,
    notifications,
    privacy,
    display,
    security,
    savedStatus,
    showPasswordSettings,
    setShowPasswordSettings,
    showLogoutModal,
    setShowLogoutModal,
    handleNotificationChange,
    handlePrivacyChange,
    handleDisplayChange,
    handleSecurityChange,
    handleLogout,
    confirmLogout,

    // password change states & handlers
    passwordFields,
    passwordLoading,
    passwordError,
    passwordSuccess,
    showOldPassword,
    setShowOldPassword,
    showNewPassword,
    setShowNewPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    handlePasswordFieldChange,
    handleChangePassword,
  } = useSettingsState({ onLogout: logout });


  const styles = getSettingsStyles({ theme, isMobile, isRTL });

  // Settings has no server-side data to fetch — everything renders from local
  // state instantly. Show a brief page-shaped skeleton anyway (for visual
  // consistency with every other page in the app) instead of a real loading
  // gate, since there's nothing async to actually wait on.
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setPageReady(true), 450);
    return () => clearTimeout(timer);
  }, []);
  // Password rules come from utils/passwordPolicy — the same ones the emailed
  // activation / reset link enforces on PasswordResetPage.
  const newPasswordMeetsPolicy = isPasswordValid(passwordFields.newPassword);
  const newPasswordsMatch =
    passwordFields.confirmPassword.length > 0 &&
    passwordFields.newPassword === passwordFields.confirmPassword;
  const canSubmitPassword =
    !passwordLoading &&
    passwordFields.oldPassword.length > 0 &&
    newPasswordMeetsPolicy &&
    newPasswordsMatch;

  const closePasswordModal = () => {
    setShowPasswordSettings(false);
    handlePasswordFieldChange("oldPassword", "");
    handlePasswordFieldChange("newPassword", "");
    handlePasswordFieldChange("confirmPassword", "");
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <div style={styles.iconWrapper}>
              <Settings
                size={isMobile ? 28 : 36}
                color="#ffffff"
                strokeWidth={2.5}
              />
            </div>
            <div style={styles.titleWrapper}>
              <h1 style={styles.title}>{t.title}</h1>
              {/*<p style={styles.subtitle}>{t.subtitle}</p>*/}
            </div>
          </div>

          {/* Logout Button */}
          <div
            style={{
              ...styles.settingItem,
              justifyContent: isMobile
                ? "center"
                : isRTL
                  ? "flex-start"
                  : "flex-end",
              paddingTop: "16px",
              borderBottom: "none",
            }}
          >
            <button
              onClick={handleLogout}
              style={styles.logoutButton}
              title={t.logout}
            >
              <LogOut size={16} />
              <span style={{ fontWeight: 800 }}>{t.logout}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(10px)",
            padding: isMobile ? "20px" : "40px",
          }}
          onClick={() => setShowLogoutModal(false)}
        >
          <div
            style={{
              background:
                theme === "light"
                  ? "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)"
                  : "linear-gradient(135deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)",
              border:
                theme === "light"
                  ? "2px solid rgba(226, 232, 240, 0.8)"
                  : "2px solid rgba(71, 85, 105, 0.4)",
              borderRadius: isMobile ? "20px" : "28px",
              padding: isMobile ? "24px" : "40px",
              maxWidth: "600px",
              width: "100%",
              backdropFilter: "blur(24px)",
              boxShadow:
                theme === "light"
                  ? "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                  : "0 25px 50px -12px rgba(0, 0, 0, 0.7)",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <button
                onClick={confirmLogout}
                style={{
                  background:
                    "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "14px",
                  padding: isMobile ? "14px 28px" : "12px 32px",
                  fontWeight: "700",
                  fontSize: isMobile ? "15px" : "16px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  flex: isMobile ? "none" : 1,
                }}
              >
                {isRTL ? "تأكيد الخروج" : "Confirm Logout"}
              </button>

              <button
                onClick={() => setShowLogoutModal(false)}
                style={{
                  background:
                    theme === "light"
                      ? "rgba(226, 232, 240, 0.8)"
                      : "rgba(71, 85, 105, 0.5)",
                  color: theme === "light" ? "#1e293b" : "#ffffff",
                  border: "none",
                  borderRadius: "14px",
                  padding: isMobile ? "14px 28px" : "12px 32px",
                  fontWeight: "700",
                  fontSize: isMobile ? "15px" : "16px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  flex: isMobile ? "none" : 1,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
              >
                {isRTL ? "إلغاء" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.settingsGrid}>
        {/* Language Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <Globe size={isMobile ? 18 : 22} />
              {t.language.title}
            </h3>
            <p style={styles.cardDescription}>{t.language.description}</p>
          </div>
          <div style={styles.settingGroup}>
            <div style={styles.settingItem}>
              <label style={styles.settingLabel}>
                <div style={styles.labelContent}>
                  <span style={styles.labelTitle}>
                    {t.language.displayLanguage}
                  </span>
                  <span style={styles.labelDescription}>
                    {t.language.languageDescription}
                  </span>
                </div>
              </label>
              <div style={styles.themeToggle}>
                <button
                  style={{
                    ...styles.themeButton,
                    ...(language === "en" ? styles.themeButtonActive : {}),
                  }}
                  onClick={() =>
                    toggleLanguage({ target: { value: "en" } })
                  }
                >
                  <Globe size={isMobile ? 16 : 18} />
                  {t.language.english}
                </button>
                <button
                  style={{
                    ...styles.themeButton,
                    ...(language === "ar" ? styles.themeButtonActive : {}),
                  }}
                  onClick={() =>
                    toggleLanguage({ target: { value: "ar" } })
                  }
                >
                  <Globe size={isMobile ? 16 : 18} />
                  {t.language.arabic}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Appearance Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <Palette size={isMobile ? 18 : 22} />
              {t.appearance.title}
            </h3>
            <p style={styles.cardDescription}>{t.appearance.description}</p>
          </div>
          <div style={styles.settingGroup}>
            <div style={styles.settingItem}>
              <label style={styles.settingLabel}>
                <div style={styles.labelContent}>
                  <span style={styles.labelTitle}>
                    {t.appearance.themeMode}
                  </span>
                  <span style={styles.labelDescription}>
                    {t.appearance.themeDescription}
                  </span>
                </div>
              </label>
              <div style={styles.themeToggle}>
                <button
                  style={{
                    ...styles.themeButton,
                    ...(theme === "light" ? styles.themeButtonActive : {}),
                  }}
                  onClick={() => setTheme("light")}
                >
                  <Sun size={isMobile ? 16 : 18} />
                  {t.appearance.light}
                </button>
                <button
                  style={{
                    ...styles.themeButton,
                    ...(theme === "dark" ? styles.themeButtonActive : {}),
                  }}
                  onClick={() => setTheme("dark")}
                >
                  <Moon size={isMobile ? 16 : 18} />
                  {t.appearance.dark}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <Bell size={isMobile ? 18 : 22} />
              {t.notifications.title}
            </h3>
            <p style={styles.cardDescription}>{t.notifications.description}</p>
          </div>
          <div style={styles.settingGroup}>
            <div style={styles.settingItem}>
              <label style={styles.settingLabel}>
                <div style={styles.labelContent}>
                  <span style={styles.labelTitle}>
                    {t.notifications.disableNotifications}
                  </span>
                  <span style={styles.labelDescription}>
                    {t.notifications.disableNotificationsDescription}
                  </span>
                </div>
              </label>
              <div
                style={{
                  display: 'flex',
                  justifyContent: isRTL ? 'flex-end' : 'flex-start',
                  alignItems: 'center'
                }}
              >
                <div
                  onClick={() => setNotificationsDisabled(!notificationsDisabled)}
                  style={{
                    backgroundColor: notificationsDisabled ? '#ef4444' : (theme === 'light' ? '#e2e8f0' : '#475569'),
                    position: 'relative',
                    width: '44px',
                    height: '24px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: notificationsDisabled ? (isRTL ? '2px' : '22px') : (isRTL ? '22px' : '2px'),
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#fff',
                      borderRadius: '50%',
                      transition: 'left 0.3s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mini Chat Bar Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <MessageCircle size={isMobile ? 18 : 22} />
              {t.miniChat.title}
            </h3>
            <p style={styles.cardDescription}>{t.miniChat.description}</p>
          </div>
          <div style={styles.settingGroup}>
            <div style={styles.settingItem}>
              <label style={styles.settingLabel}>
                <div style={styles.labelContent}>
                  <span style={styles.labelTitle}>
                    {t.miniChat.showMiniChat}
                  </span>
                  <span style={styles.labelDescription}>
                    {t.miniChat.showMiniChatDescription}
                  </span>
                </div>
              </label>
              <div
                style={{
                  display: 'flex',
                  justifyContent: isRTL ? 'flex-end' : 'flex-start',
                  alignItems: 'center'
                }}
              >
                <div
                  onClick={() => setMiniChatDisabled(!miniChatDisabled)}
                  style={{
                    backgroundColor: !miniChatDisabled ? '#22c55e' : (theme === 'light' ? '#e2e8f0' : '#475569'),
                    position: 'relative',
                    width: '44px',
                    height: '24px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: !miniChatDisabled ? (isRTL ? '2px' : '22px') : (isRTL ? '22px' : '2px'),
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#fff',
                      borderRadius: '50%',
                      transition: 'left 0.3s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company Badge Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <Smartphone size={isMobile ? 18 : 22} />
              {t.clientBadge.title}
            </h3>
            <p style={styles.cardDescription}>{t.clientBadge.description}</p>
          </div>
          <div style={styles.settingGroup}>
            <div style={styles.settingItem}>
              <label style={styles.settingLabel}>
                <div style={styles.labelContent}>
                  <span style={styles.labelTitle}>
                    {t.clientBadge.showBadge}
                  </span>
                  <span style={styles.labelDescription}>
                    {t.clientBadge.showBadgeDescription}
                  </span>
                </div>
              </label>
              <div
                style={{
                  display: 'flex',
                  justifyContent: isRTL ? 'flex-end' : 'flex-start',
                  alignItems: 'center'
                }}
              >
                <div
                  onClick={() => setClientBadgeDisabled(!clientBadgeDisabled)}
                  style={{
                    backgroundColor: !clientBadgeDisabled ? '#22c55e' : (theme === 'light' ? '#e2e8f0' : '#475569'),
                    position: 'relative',
                    width: '44px',
                    height: '24px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: !clientBadgeDisabled ? (isRTL ? '2px' : '22px') : (isRTL ? '22px' : '2px'),
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#fff',
                      borderRadius: '50%',
                      transition: 'left 0.3s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Privacy & Security Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <Shield size={isMobile ? 18 : 22} />
              {t.privacy.title}
            </h3>
            <p style={styles.cardDescription}>{t.privacy.description}</p>
          </div>
          <div style={styles.settingGroup}>
            <div style={{
              ...styles.settingItem,
              borderBottom: "none",
              paddingBottom: 0,
            }}>
              <label style={styles.settingLabel}>
                <div style={styles.labelContent}>
                  <span style={styles.labelTitle}>
                    {t.privacy.password}
                  </span>
                  <span style={styles.labelDescription}>
                    {t.privacy.lastChanged}
                  </span>
                </div>
              </label>
              <button
                onClick={() => setShowPasswordSettings(true)}
                style={{
                  ...styles.themeButton,
                  background: "linear-gradient(135deg, #2e7cba 0%, #2e7cba 100%)",
                  color: "#ffffff",
                  padding: isMobile ? "12px 20px" : "10px 24px",
                  borderRadius: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: isMobile ? "13px" : "14px",
                  width: isMobile ? "100%" : "auto",
                  justifyContent: "center",
                  transition: "all 0.3s ease",
                  transform: "scale(1)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.9";
                  e.currentTarget.style.transform = "scale(1.02)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <Lock size={16} />
                <span>{t.privacy.changePassword}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Change Password Modal */}
        {showPasswordSettings && (
          <div
            className="settings-password-modal-scroll"
            style={styles.passwordModalOverlay}
            onClick={closePasswordModal}
          >
            <div
              className="settings-password-modal-scroll"
              style={styles.passwordModalCard}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <style>{`
                @keyframes settings-spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
                .settings-spinner {
                  animation: settings-spin 1s linear infinite;
                }
                /* The app sets a global 8px ::-webkit-scrollbar, which shows
                   up as a visible track down the side of the dialog. Scrolling
                   still works — the bar just isn't painted. */
                .settings-password-modal-scroll::-webkit-scrollbar {
                  width: 0;
                  height: 0;
                  display: none;
                }
              `}</style>

              <button
                type="button"
                onClick={closePasswordModal}
                style={styles.passwordModalClose}
                aria-label={isRTL ? "إغلاق" : "Close"}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.75"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
              >
                <X size={18} />
              </button>

              <div style={styles.passwordModalHeader}>
                <div style={styles.passwordModalBadge}>
                  <Lock size={22} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3 style={styles.passwordModalTitle}>{t.privacy.changePassword}</h3>
                  <p style={styles.passwordModalSubtitle}>
                    {t.privacy.changePasswordSubtitle}
                  </p>
                </div>
              </div>

              <form
                onSubmit={handleChangePassword}
                style={{ display: "flex", flexDirection: "column", gap: "18px" }}
              >
                {/* Current Password Field */}
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>{t.privacy.currentPassword}</label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputLeadIcon}>
                      <Lock size={17} />
                    </span>
                    <input
                      type={showOldPassword ? "text" : "password"}
                      value={passwordFields.oldPassword}
                      onChange={(e) => handlePasswordFieldChange("oldPassword", e.target.value)}
                      style={styles.input}
                      placeholder={t.privacy.enterCurrentPassword}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      style={styles.inputIcon}
                      aria-label={t.privacy.currentPassword}
                    >
                      {showOldPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* New Password Field */}
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>{t.privacy.newPassword}</label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputLeadIcon}>
                      <Lock size={17} />
                    </span>
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={passwordFields.newPassword}
                      onChange={(e) => handlePasswordFieldChange("newPassword", e.target.value)}
                      style={{
                        ...styles.input,
                        borderColor:
                          passwordFields.newPassword.length === 0
                            ? undefined
                            : newPasswordMeetsPolicy
                              ? "rgba(16, 185, 129, 0.6)"
                              : "rgba(239, 68, 68, 0.45)",
                      }}
                      placeholder={t.privacy.enterNewPassword}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      style={styles.inputIcon}
                      aria-label={t.privacy.newPassword}
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Live checklist of the shared password rules */}
                <PasswordRequirements
                  password={passwordFields.newPassword}
                  language={language}
                  theme={theme}
                  title={t.privacy.passwordRequirementsTitle}
                />

                {/* Confirm New Password Field */}
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>{t.privacy.confirmPassword}</label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.inputLeadIcon}>
                      <Lock size={17} />
                    </span>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwordFields.confirmPassword}
                      onChange={(e) => handlePasswordFieldChange("confirmPassword", e.target.value)}
                      style={{
                        ...styles.input,
                        borderColor:
                          passwordFields.confirmPassword.length === 0
                            ? undefined
                            : newPasswordsMatch
                              ? "rgba(16, 185, 129, 0.6)"
                              : "rgba(239, 68, 68, 0.45)",
                      }}
                      placeholder={t.privacy.enterConfirmPassword}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={styles.inputIcon}
                      aria-label={t.privacy.confirmPassword}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>

                  {passwordFields.confirmPassword.length > 0 && (
                    newPasswordsMatch ? (
                      <span style={styles.successMessage}>
                        <CheckCircle2 size={15} style={{ flexShrink: 0 }} />
                        {t.privacy.passwordsMatch}
                      </span>
                    ) : (
                      <span style={{ ...styles.successMessage, color: "#ef4444" }}>
                        <AlertCircle size={15} style={{ flexShrink: 0 }} />
                        {t.privacy.passwordsDoNotMatch}
                      </span>
                    )
                  )}
                </div>

                {/* Error Message */}
                {passwordError && (
                  <div style={styles.errorMessage}>
                    <AlertCircle size={16} style={{ flexShrink: 0 }} />
                    <span>{passwordError}</span>
                  </div>
                )}

                {/* Success Modal */}
                <SaveSuccessModal
                  isOpen={!!passwordSuccess}
                  theme={theme}
                  language={language}
                  message={passwordSuccess}
                />

                {/* Modal Actions */}
                <div style={styles.passwordModalActions}>
                  <button
                    type="button"
                    onClick={closePasswordModal}
                    style={styles.cancelButton}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.8"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                  >
                    {isRTL ? "إلغاء" : "Cancel"}
                  </button>

                  <button
                    type="submit"
                    disabled={!canSubmitPassword}
                    style={{
                      ...styles.submitButton,
                      flex: 1,
                      marginTop: 0,
                      opacity: canSubmitPassword ? 1 : 0.55,
                      cursor: canSubmitPassword ? "pointer" : "not-allowed",
                    }}
                  >
                    {passwordLoading ? (
                      <>
                        <RefreshCw className="settings-spinner" size={16} />
                        <span>{language === "ar" ? "جاري الحفظ..." : "Saving..."}</span>
                      </>
                    ) : (
                      <>
                        <Lock size={16} />
                        <span>{t.privacy.savePasswordButton}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedSettingsPage;